// Status pills for the wizard (Module 02 §2.1/§2.2 statuses + the live badge),
// re-based onto the design-system `StatusChip` (§3.5, UX-1). Semantic mapping
// is product-wide law: green = confirmed/live, amber = needs-review, neutral =
// in progress, gray = not started.
//
// Deliberately separate from `components/status-badge.tsx`: that one carries
// the catalogue's lifecycle vocabulary. These read the onboarding namespace.

import { useTranslation } from "react-i18next";
import { StatusChip, type ChipVariant } from "../../../components/status-chip";
import { formatTimestamp } from "../../../lib/datetime";
import type { SessionStatus, StepStatus } from "../lib/wizard-steps";

const SESSION_VARIANT: Record<SessionStatus, ChipVariant> = {
  draft: "neutral",
  in_progress: "accent",
  ready_for_review: "warn",
  live: "ok",
};

const STEP_VARIANT: Record<StepStatus, ChipVariant> = {
  not_started: "neutral",
  in_progress: "accent",
  completed: "ok",
};

export function SessionStatusBadge({ status }: { status: SessionStatus }) {
  const { t } = useTranslation();
  return (
    <StatusChip variant={SESSION_VARIANT[status]} data-status={status}>
      {t(`onboarding.sessionStatus.${status}`)}
    </StatusChip>
  );
}

export function StepStatusBadge({ status }: { status: StepStatus }) {
  const { t } = useTranslation();
  return (
    <StatusChip variant={STEP_VARIANT[status]} data-status={status}>
      {t(`onboarding.stepStatus.${status}`)}
    </StatusChip>
  );
}

/**
 * The "this tenant is live" badge (§4 S1). Shown from the moment
 * `session.status === 'live'`; `wentLiveAt` adds the date when the API has it.
 */
export function LiveBadge({ wentLiveAt }: { wentLiveAt?: string | null }) {
  const { t, i18n } = useTranslation();
  const date =
    wentLiveAt !== undefined && wentLiveAt !== null && wentLiveAt.length > 0
      ? formatTimestamp(wentLiveAt, i18n.language)
      : undefined;
  return (
    <span
      data-testid="live-badge"
      title={date !== undefined ? t("onboarding.shell.liveSince", { date }) : undefined}
    >
      <StatusChip variant="ok">{t("onboarding.shell.liveBadge")}</StatusChip>
    </span>
  );
}
